import { Injectable, inject } from '@angular/core';
import { MetaDataFieldResponse } from '@services/api-client/responses';
import { AgreementForm } from '../feature/shared-agreement.model';
import { toNgbDateFromString } from '@helpers/datetime-helper';
import { CachedValuesService } from '@services/cached-values';
import { MetadataHighlightsService } from './metadata-highlights.service';
import { ApiClientService } from '@services/api-client';
import { firstValueFrom, BehaviorSubject } from 'rxjs';
import { ValidationFailure } from '../components/validation-failures-modal/validation-failures-modal.component';
import { RiskAnalysis } from '../components/risk-analysis/risk-analysis.component';
import { TextReference } from '../types/text-reference.type';

export type MetadataResult = {
    validationFailures: ValidationFailure[] | null;
    riskAnalysis: RiskAnalysis | null;
    failedCounterpartNames: string[];
};

export enum AgreementMetaDataConstants {
    Description = 0,
    Name = 1,
    AgreementNumber = 2,
    ValidFrom = 3,
    ValidTo = 4,
    Area = 5,
    Category = 6,
    PaymentTerms = 7,
    EstimatedAnnualValue = 8,
    InvoiceInterval = 9,
    Period = 10,
    NoticePeriod = 11,
    PeriodAfterExtension = 12,
    NoticePeriodAfterExtension = 13,
    Summary = 14,
    CustomerName = 15,
    CounterpartName = 16,
    CustomerResponsible = 17,
    CounterpartResponsible = 18,
    CustomerSigned = 19,
    CounterpartSigned = 20,
    RiskAnalysis = 90,
    ValidateFails = 99
}

// Utility functions for working with the enum
export class AgreementMetaDataUtils {
    /**
     * Get all enum keys as strings
     */
    public static getEnumKeys(): string[] {
        return Object.keys(AgreementMetaDataConstants).filter((key) => isNaN(Number(key)));
    }

    /**
     * Get enum string key by numeric value
     */
    public static getEnumKeyByValue(value: AgreementMetaDataConstants): string {
        return AgreementMetaDataConstants[value];
    }

    /**
     * Get enum numeric value by string key
     */
    public static getEnumValueByKey(key: string): AgreementMetaDataConstants | undefined {
        return AgreementMetaDataConstants[key as keyof typeof AgreementMetaDataConstants];
    }

    /**
     * Check if a string is a valid enum key
     */
    public static isValidEnumKey(key: string): key is keyof typeof AgreementMetaDataConstants {
        return key in AgreementMetaDataConstants && isNaN(Number(key));
    }

    /**
     * Get all enum entries as key-value pairs
     */
    public static getEnumEntries(): { key: string; value: AgreementMetaDataConstants }[] {
        return this.getEnumKeys().map((key) => ({
            key,
            value: this.getEnumValueByKey(key)
        }));
    }
}

@Injectable({
    providedIn: 'root'
})
export class AgreementMetadataGenerationService {
    private readonly apiClientService = inject(ApiClientService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly highlightsService = inject(MetadataHighlightsService);

    // BehaviorSubject to track pending counterpart names that need to be created
    private readonly pendingCounterpartName$ = new BehaviorSubject<string | null>(null);

    /**
     * Get the observable of pending counterpart names
     */
    public getPendingCounterpartName$() {
        return this.pendingCounterpartName$.asObservable();
    }

    /**
     * Set a counterpart name that needs to be created
     */
    public setPendingCounterpartName(name: string | null) {
        console.log('setPendingCounterpartName:', name);
        this.pendingCounterpartName$.next(name);
    }

    /**
     * Clear the pending counterpart name
     */
    public clearPendingCounterpartName() {
        console.log('clearPendingCounterpartName');
        this.pendingCounterpartName$.next(null);
    }

    public async setFormValuesFromMetadataList(
        form: AgreementForm,
        metadataList: MetaDataFieldResponse[]
    ): Promise<MetadataResult> {
        const textReferences = new Map<string, TextReference>();
        const failedCounterpartNames: string[] = [];

        // Check for validation failures and risk analysis first
        const validationFailuresItem = metadataList.find((field) => field.property === 'ValidateFails');
        const riskAnalysisItem = metadataList.find((field) => field.property === 'RiskAnalysis');
        //console.log('RiskAnalysis metadata item:', riskAnalysisItem);

        let validationFailures: ValidationFailure[] | null = null;
        if (validationFailuresItem?.value) {
            try {
                validationFailures = JSON.parse(validationFailuresItem.value) as ValidationFailure[];
            } catch (error) {
                console.error('Error parsing validation failures:', error);
            }
        }

        let riskAnalysis: RiskAnalysis | null = null;
        if (riskAnalysisItem?.value) {
            try {
                riskAnalysis = JSON.parse(riskAnalysisItem.value) as RiskAnalysis;
                //console.log('Parsed risk analysis:', riskAnalysis);
            } catch (error) {
                console.error('Error parsing risk analysis:', error);
            }
        }

        for (const enumKey of Object.keys(AgreementMetaDataConstants).filter((key) => isNaN(Number(key)))) {
            try {
                const enumValue = AgreementMetaDataConstants[enumKey as keyof typeof AgreementMetaDataConstants];
                const metadataItem = metadataList.find((field) => field.property === enumKey);
                const actualValue = metadataItem?.value || '';

                if (actualValue) {
                    //console.log(`Processing ${enumKey} (${enumValue}): ${actualValue}`);

                    if (await this.processAgreementMetadata(form, enumValue, actualValue, failedCounterpartNames)) {
                        // Create text reference for PDF highlighting
                        // Use sourceText from server if available (exact text from PDF),
                        // otherwise fall back to the processed value
                        textReferences.set(enumKey, {
                            text: metadataItem?.sourceText || actualValue,
                            fieldName: enumKey
                        });
                    }
                }
            } catch (error) {
                console.error(`Error processing metadata for key ${enumKey}:`, error);
            }
        }

        // Set text references in the service (automatically updates highlighted fields)
        this.highlightsService.setFieldTextReferences(textReferences);

        return {
            validationFailures,
            riskAnalysis,
            failedCounterpartNames
        };
    }

    public clearHighlights() {
        this.highlightsService.clearHighlights();
        this.highlightsService.clearTextReferences();
    }

    private async processAgreementMetadata(
        form: AgreementForm,
        metadataType: AgreementMetaDataConstants,
        value: string,
        failedCounterpartNames?: string[]
    ): Promise<boolean> {
        if (!value) return false;
        switch (metadataType) {
            case AgreementMetaDataConstants.Description:
                form.details.controls.description.setValue(value);
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.Name:
                form.details.controls.name.setValue(value);
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.AgreementNumber:
                form.details.controls.agreementNo.setValue(value);
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.ValidFrom:
                form.period.controls.validFrom.setValue(toNgbDateFromString(value));
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.ValidTo:
                form.period.controls.validTo.setValue(toNgbDateFromString(value));
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.Area:
                try {
                    const items = await firstValueFrom(this.apiClientService.getTypeListItems$('AREA'));
                    let item = items.find((c) => c.value.toLowerCase() === value.toLowerCase());
                    if (!item) {
                        item = items.find((c) => value.toLowerCase().includes(c.value.toLowerCase()));
                    }
                    if (item) {
                        form.details.controls.area.setValue(item.name);
                        form.details.markAsDirty();
                        console.log(`Found list type value: ${value}`);
                        return true;
                    } else {
                        console.log(`Area not found: ${value}`);
                        return false;
                    }
                } catch (error) {
                    console.error('Error fetching list types:', error);
                    return false;
                }
            case AgreementMetaDataConstants.Category:
                try {
                    const items = await firstValueFrom(this.apiClientService.getTypeListItems$('CATEGORY'));
                    let item = items.find((c) => c.value.toLowerCase() === value.toLowerCase());
                    // If exact match not found, try contains
                    if (!item) {
                        item = items.find((c) => value.toLowerCase().includes(c.value.toLowerCase()));
                    }
                    if (item) {
                        form.details.controls.category.setValue(item.name);
                        form.details.markAsDirty();
                        console.log(`Found category list type value: ${value}`);
                        return true;
                    } else {
                        console.log(`Category not found: ${value}`);
                        return false;
                    }
                } catch (error) {
                    console.error('Error fetching category list types:', error);
                    return false;
                }
            case AgreementMetaDataConstants.PaymentTerms: {
                const paymentTerms = parseInt(value); // this.parseDurationString(value);
                if (paymentTerms) {
                    form.payment.controls.paymentTerms.setValue(paymentTerms);
                    form.payment.markAsDirty();
                    console.log(`Parsed payment terms: ${paymentTerms}`);
                } else {
                    return false;
                }
                break;
            }
            case AgreementMetaDataConstants.EstimatedAnnualValue: {
                // 150 000 SEK
                const paymentValue = this.parseValueString(value);
                if (paymentValue) {
                    form.payment.controls.value.setValue(paymentValue.number);
                    form.payment.controls.currency.setValue(paymentValue.unit?.trim() || 'SEK');
                    form.payment.markAsDirty();
                    console.log(`Parsed estimated annual value: ${paymentValue.number} ${paymentValue.unit}`);
                } else {
                    return false;
                }
                break;
            }
            case AgreementMetaDataConstants.InvoiceInterval:
                console.log('InvoiceInterval parsing value:', value);
                //  löpande, veckovis, månadsvis, kvartalsvis, årsvis
                if (value.toLowerCase().includes('löpande')) form.payment.controls.invoiceIntervalId.setValue(1);
                if (value.toLowerCase().includes('veckovis')) form.payment.controls.invoiceIntervalId.setValue(2);
                if (value.toLowerCase().includes('månadsvis')) form.payment.controls.invoiceIntervalId.setValue(3);
                if (value.toLowerCase().includes('kvartalsvis')) form.payment.controls.invoiceIntervalId.setValue(4);
                if (value.toLowerCase().includes('årsvis')) form.payment.controls.invoiceIntervalId.setValue(6);
                break;
            case AgreementMetaDataConstants.Period: {
                const contractDuration = parseInt(value); // this.parseDurationString(value);
                if (contractDuration) {
                    form.period.controls.commitmentPeriodTypeId.setValue(contractDuration);
                    console.log(`Parsed contract duration: ${contractDuration}`);
                } else {
                    //form.period.controls.commitmentPeriodTypeId.setValue(0);
                    return false;
                }
                break;
            }
            case AgreementMetaDataConstants.NoticePeriod: {
                const noticePeriod = this.parseDurationString(value);
                if (noticePeriod) {
                    form.period.controls.noticePeriod.setValue(noticePeriod.number);
                    form.period.controls.noticePeriodTypeId.setValue(noticePeriod.unit);
                    form.period.markAsDirty();
                    console.log(`Parsed notice period: ${noticePeriod.number} ${noticePeriod.unit}`);
                } else {
                    return false;
                }
                break;
            }
            case AgreementMetaDataConstants.PeriodAfterExtension: {
                const contractDurationAfterExt = this.parseDurationString(value);
                if (contractDurationAfterExt) {
                    // Set the numeric value
                    form.period.controls.periodAfterExtension.setValue(contractDurationAfterExt.number);
                    form.period.controls.periodTypeIdAfterExtension.setValue(contractDurationAfterExt.unit);
                    form.period.markAsDirty();
                    console.log(
                        `Parsed contract duration after extension: ${contractDurationAfterExt.number} ${contractDurationAfterExt.unit}`
                    );
                } else {
                    return false;
                }
                break;
            }
            case AgreementMetaDataConstants.NoticePeriodAfterExtension: {
                const noticePeriod = this.parseDurationString(value);
                if (noticePeriod) {
                    form.period.controls.noticePeriodAfterExtension.setValue(noticePeriod.number);
                    form.period.controls.noticePeriodTypeIdAfterExtension.setValue(noticePeriod.unit);
                    form.period.markAsDirty();
                    console.log(`Parsed notice period: ${noticePeriod.number} ${noticePeriod.unit}`);
                }
                break;
            }
            case AgreementMetaDataConstants.Summary:
                break;
            case AgreementMetaDataConstants.CustomerName:
                try {
                    const customers = await firstValueFrom(this.cachedValuesService.customers$);
                    let customer = customers.find((c) => c.name.toLowerCase() === value.toLowerCase());
                    if (!customer) {
                        customer = customers.find((c) => value.includes(c.name.toLowerCase()));
                    }
                    if (customer) {
                        form.details.controls.customerId.setValue(customer.id);
                        form.details.markAsDirty();
                        console.log(`Found customer ID: ${customer.id} for name: ${value}`);
                        return true;
                    } else {
                        console.log(`Customer not found: ${value}`);
                        return false;
                    }
                } catch (error) {
                    console.error('Error fetching customers from cache:', error);
                    return false;
                }
            case AgreementMetaDataConstants.CounterpartName:
                try {
                    const counterparts = await firstValueFrom(this.cachedValuesService.counterparts$);
                    let counterpart = counterparts.find((c) => c.companyName.toLowerCase() === value.toLowerCase());
                    if (!counterpart) {
                        counterpart = counterparts.find((c) => value.includes(c.companyName.toLowerCase()));
                    }
                    if (counterpart) {
                        form.details.controls.counterpartId.setValue(counterpart.id);
                        form.details.markAsDirty();
                        console.log(`Found counterpart ID: ${counterpart.id} for name: ${value}`);
                        return true;
                    } else {
                        console.log(`Counterpart not found: ${value}`);
                        // Track the failed counterpart name for automatic creation
                        if (failedCounterpartNames && !failedCounterpartNames.includes(value)) {
                            failedCounterpartNames.push(value);
                        }
                        return false;
                    }
                } catch (error) {
                    console.error('Error fetching counterparts from cache:', error);
                    return false;
                }
            case AgreementMetaDataConstants.CustomerResponsible:
                try {
                    const users = await firstValueFrom(this.cachedValuesService.users$);
                    let user = users.find(
                        (c) => `${c.firstName.toLowerCase()} ${c.lastName.toLowerCase()}` === value.toLowerCase()
                    );
                    if (!user) {
                        user = users.find((c) =>
                            value.includes(`${c.firstName.toLowerCase()} ${c.lastName.toLowerCase()}`)
                        );
                    }
                    if (user) {
                        form.details.controls.responsibleId.setValue(user.id);
                        form.details.markAsDirty();
                        console.log(`Found responsible ID: ${user.id} for name: ${value}`);
                        return true;
                    } else {
                        console.log(`Responsible not found: ${value}`);
                        return false;
                    }
                } catch (error) {
                    console.error('Error fetching users from cache:', error);
                    return false;
                }
            case AgreementMetaDataConstants.CounterpartResponsible:
                break;
            case AgreementMetaDataConstants.CustomerSigned:
                form.details.controls.signer.setValue(value);
                form.details.markAsDirty();
                break;
            case AgreementMetaDataConstants.CounterpartSigned:
                break;
            default:
                return false;
        }
        return true;
    }

    private parseDurationString(value: string): { number: number; unit: number } | null {
        if (!value) return null;

        const match = value.trim().match(/^(\d+)\s+(\w+)$/);
        if (!match) return null;

        const number = parseInt(match[1], 10);
        const unit = match[2].toLowerCase() === 'months' || match[2].toLowerCase() === 'month' ? 1 : 0; // 1 for months, 0 for days

        return { number, unit };
    }

    private parseValueString(value: string): { number: number; unit: string } | null {
        if (!value) return null;

        // Handle formats: 150 000 SEK, 1000 USD, 2000, 1200.00 SEK, 150,000.50 USD
        const trimmedValue = value.trim();

        // Pattern to match: optional number with spaces/commas/decimals, optional currency
        const match = trimmedValue.match(/^([\d\s,\\.]+)(?:\s+([A-Za-z]{3}))?$/);
        if (!match) return null;

        // Clean the number string by removing spaces and commas
        const numberStr = match[1].replace(/[\s,]/g, '');
        const number = parseFloat(numberStr);
        const unit = match[2] || ''; // Empty string if no currency provided

        if (isNaN(number)) return null;

        return { number, unit };
    }
}
